/**
 * DeepL API Provider
 * Routes through backend proxy to avoid CORS issues
 * Backend: https://api.linguaproz.com/api/translate/deepl
 */

import { MTProvider } from "./mtEngine";
import { ApiRateLimiter } from "./apiRateLimiter";
import { mtFetch } from "@/lib/mtClient";

const LANG_NAME_TO_CODE: Record<string, string> = {
    english: "en",
    afrikaans: "af",
    portuguese: "pt",
    french: "fr",
    spanish: "es",
    german: "de",
    dutch: "nl",
    italian: "it",
    arabic: "ar",
    chinese: "zh",
    japanese: "ja",
    korean: "ko",
    russian: "ru",
    polish: "pl",
    swedish: "sv",
    norwegian: "no",
    danish: "da",
    finnish: "fi",
    czech: "cs",
    hungarian: "hu",
    romanian: "ro",
    greek: "el",
    turkish: "tr",
    hebrew: "he",
    hindi: "hi",
    thai: "th",
    indonesian: "id",
    malay: "ms",
    swahili: "sw",
    zulu: "zu",
    xhosa: "xh",
    sotho: "st",
    tswana: "tn",
    venda: "ve",
    tsonga: "ts",
    ndebele: "nr",
};

const normaliseCode = (lang: string): string => {
    if (!lang) return lang;
    if (lang.length <= 5 && !lang.includes(" ")) return lang.toLowerCase();
    return LANG_NAME_TO_CODE[lang.toLowerCase()] ?? lang.toLowerCase();
};

const BACKEND_URL = `${(import.meta as any).env?.VITE_API_URL || "https://api.linguaproz.com"}/api/translate/deepl`;

class DeepLProvider implements MTProvider {
    private rateLimiter: ApiRateLimiter;
    private readonly MONTHLY_LIMIT = 500000;
    private readonly BATCH_SIZE = 50;

    constructor(_apiKey?: string, _isFreeAccount = true) {
        // API key is now handled by the backend — not needed in frontend
        this.rateLimiter = new ApiRateLimiter("deepl", this.MONTHLY_LIMIT);
    }

    getName(): string {
        return "DeepL";
    }
    isAvailable(): boolean {
        return true;
    }
    getUsage(): number {
        return this.rateLimiter.getUsage();
    }
    getMonthlyLimit(): number {
        return this.MONTHLY_LIMIT;
    }
    resetUsage(): void {
        this.rateLimiter.resetUsage();
    }

    async translate(
        text: string,
        sourceLang: string,
        targetLang: string,
    ): Promise<string> {
        if (!text || text.trim().length === 0) return "";

        const charCount = text.length;
        if (!this.rateLimiter.canMakeRequest(charCount)) {
            throw new Error(
                `Rate limit exceeded. Used: ${this.getUsage()}/${this.MONTHLY_LIMIT} chars this month.`,
            );
        }

        const response = await mtFetch(BACKEND_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                text,
                source_lang:
                    sourceLang !== "auto"
                        ? normaliseCode(sourceLang)
                        : undefined,
                target_lang: normaliseCode(targetLang),
                tag_handling: "html",
            }),
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(
                err.error || `DeepL error: HTTP ${response.status}`,
            );
        }

        const data = await response.json();
        if (!data.translations?.[0]?.text)
            throw new Error("Invalid response from DeepL");

        this.rateLimiter.trackUsage(charCount);
        return data.translations[0].text;
    }

    async translateBatch(
        texts: string[],
        sourceLang: string,
        targetLang: string,
    ): Promise<string[]> {
        if (texts.length === 0) return [];

        const validTexts = texts.filter((t) => t && t.trim().length > 0);
        if (validTexts.length === 0) return texts.map(() => "");

        const totalChars = validTexts.join("").length;
        if (!this.rateLimiter.canMakeRequest(totalChars)) {
            throw new Error(
                `Rate limit exceeded. Used: ${this.getUsage()}/${this.MONTHLY_LIMIT} chars this month.`,
            );
        }

        const allTranslations: string[] = [];

        for (let i = 0; i < validTexts.length; i += this.BATCH_SIZE) {
            const batch = validTexts.slice(i, i + this.BATCH_SIZE);

            const response = await mtFetch(BACKEND_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    text: batch,
                    source_lang:
                        sourceLang !== "auto"
                            ? normaliseCode(sourceLang)
                            : undefined,
                    target_lang: normaliseCode(targetLang),
                    tag_handling: "html",
                }),
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(
                    err.error || `DeepL error: HTTP ${response.status}`,
                );
            }

            const data = await response.json();
            allTranslations.push(...data.translations.map((t: any) => t.text));
        }

        this.rateLimiter.trackUsage(totalChars);
        return allTranslations;
    }
}

export default DeepLProvider;
